// Client-side business-date derivation — the D-089 rule in one sentence:
// DATA SELECTION IS FACILITY-LOCAL, DISPLAY TINTING IS DELIBERATELY
// VIEWER-LOCAL.
//
// Which helper answers which half:
//
//  * `facilityTodayIso` — every date a client puts INTO A QUERY OR A DEFAULT
//    that selects business data (a `shift_date`, a cost run's `as_of_date`
//    default) is computed in the FACILITY's calendar. The staging-proven
//    failure (D-089, spec 04 §4 row 12): the wall board derived its polled
//    dates with the browser's calendar and, for any viewer west of the plant,
//    missed the running shift's date entirely for the first hours of every
//    facility day.
//
//  * `todayIso` — a browser-local FLOOR on a date PICKER (the BOM/routing
//    activation dialogs' "no back-dating" tint and `min=`) deliberately stays
//    viewer-local: it is a courtesy hint on the viewer's own input, the server
//    re-validates the business date authoritatively in the tenant's timezone
//    (`EFFECTIVE_DATE_PAST`, module 01 §9.2), and a facility-calendar floor
//    would REFUSE dates the server accepts (or admit ones it refuses) for part
//    of every day without making the answer any more correct. `todayIso` is
//    also `facilityTodayIso`'s fallback when no timezone is in hand — a screen
//    that is right 19 hours a day beats a blank one.
//
// Shared here (src/lib) because the consumers span areas: office/production
// (S5 scoreboard), floor-pwa (wall board), office/catalogue (cost runs,
// BOM/routing activation floors).

/** Today in the BROWSER's calendar — the deliberate viewer-local picker floor
 * (server re-validates), and the fallback when no facility timezone is known.
 * Never use this to SELECT business data when a facility is in hand — that is
 * `facilityTodayIso`'s job (D-089). */
export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** One formatter per timezone, kept across renders: the wall board recomputes
 * its dates off a 5-second tick, and `Intl.DateTimeFormat` construction is the
 * expensive half of the call. `null` records a timezone the runtime refused,
 * so a bad string is not re-thrown at every tick. */
const facilityDateFormatters = new Map<string, Intl.DateTimeFormat | null>();

/**
 * The calendar date at `nowMs` in the FACILITY's timezone — the §9.7-correct
 * derivation of "today" for every business date a client computes from its own
 * clock to SELECT data (shift-date queries and defaults, the cost run
 * `as_of_date` default).
 *
 * THE VIEWER'S CALENDAR IS THE WRONG ONE, and this is a staging-proven defect,
 * not pedantry: the wall board used to derive its two polled dates with
 * `todayIso` (browser calendar). Rendered by any viewer whose OS timezone sits
 * west of the facility — a mis-set wall tablet, or the UTC container CI runs
 * in — the board polls {facility-date−2, facility-date−1} for the first hours
 * of every facility day (00:00–05:00 Asia/Almaty for a UTC viewer) and misses
 * the running shift's date entirely, drawing stale tiles under a fresh
 * "updated seconds ago" stamp. Caught by M04-S07 on staging, 2026-08-08
 * 00:07 Almaty (spec 04 §4 row 12, D-089).
 *
 * Falls back to the browser calendar only when no timezone is in hand or the
 * runtime does not know the name — a board that renders nothing because a
 * timezone string is stale is worse than one that is right 19 hours a day.
 */
/**
 * The UTC INSTANT at which a facility-local calendar date begins — the other
 * half of the D-089 rule. `facilityTodayIso` answers "what date is it there";
 * this answers "when does that date start, on the wire". A screen that filters
 * a timestamp column by date range MUST convert both edges through the
 * facility's timezone: the S2 waste ledger used to send
 * `${day}T00:00:00Z` — the facility-local date with a Z STAPLED ON — which
 * shifts the whole window 5 hours late for Almaty, so any event captured
 * between 00:00 and 05:00 facility time fell OUTSIDE its own day's filter.
 * Caught by M05-S01 on staging, 2026-08-10 00:07 Almaty — the same
 * post-midnight window, one screen over (spec 05 §4 S2 note).
 *
 * Implementation: a millisecond binary search for the first instant that
 * RENDERS as the target date in the zone (via `facilityTodayIso`'s cached
 * formatter). ISO date strings compare lexicographically, `facilityTodayIso`
 * is monotonic in its instant for any real zone, and the bounds are seeded
 * ±27 h around the date's UTC midnight — wider than any real offset (±14 h)
 * — then widened defensively until they bracket. ~40 formatter reads per
 * call, cached formatter, called on filter changes only. Exact for :30/:45
 * zones and across DST transitions, where any offset-arithmetic shortcut has
 * to special-case. Falls back to the browser-local reading of the date when
 * no timezone is in hand — the same fallback contract as `facilityTodayIso`.
 */
export function facilityDayStartUtcMs(
  dateIso: string,
  timezone: string | null | undefined,
): number {
  const [y, m, d] = dateIso.split("-").map(Number);
  if (typeof timezone !== "string" || timezone.length === 0) {
    return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1).getTime();
  }
  const utcMidnight = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  let lo = utcMidnight - 27 * 3_600_000;
  let hi = utcMidnight + 27 * 3_600_000;
  while (facilityTodayIso(lo, timezone) >= dateIso) lo -= 24 * 3_600_000;
  while (facilityTodayIso(hi, timezone) < dateIso) hi += 24 * 3_600_000;
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (facilityTodayIso(mid, timezone) < dateIso) lo = mid;
    else hi = mid;
  }
  return hi;
}

/** The UTC instant just PAST the end of a facility-local date — i.e. the start
 * of the next facility-local day. Use as an EXCLUSIVE upper bound; for an
 * inclusive `occurred_to` on second-granularity APIs, subtract 1 ms. */
export function facilityDayEndUtcMs(dateIso: string, timezone: string | null | undefined): number {
  const [y, m, d] = dateIso.split("-").map(Number);
  const next = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + 1));
  const nextIso = `${String(next.getUTCFullYear()).padStart(4, "0")}-${String(next.getUTCMonth() + 1).padStart(2, "0")}-${String(next.getUTCDate()).padStart(2, "0")}`;
  return facilityDayStartUtcMs(nextIso, timezone);
}

export function facilityTodayIso(nowMs: number, timezone: string | null | undefined): string {
  if (typeof timezone === "string" && timezone.length > 0) {
    let fmt = facilityDateFormatters.get(timezone);
    if (fmt === undefined) {
      try {
        // en-CA is incidental: the date is assembled from parts below, the
        // locale only has to admit Gregorian numeric year/month/day.
        fmt = new Intl.DateTimeFormat("en-CA", {
          timeZone: timezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      } catch {
        fmt = null;
      }
      facilityDateFormatters.set(timezone, fmt);
    }
    if (fmt !== null) {
      const parts = fmt.formatToParts(nowMs);
      const part = (type: Intl.DateTimeFormatPartTypes): string =>
        parts.find((p) => p.type === type)?.value ?? "";
      const y = part("year");
      const m = part("month");
      const d = part("day");
      if (y !== "" && m !== "" && d !== "") {
        return `${y.padStart(4, "0")}-${m}-${d}`;
      }
    }
  }
  return todayIso(new Date(nowMs));
}
